import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Video, Lock, Clock, KeyRound, Loader2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const extractToken = (s) => {
  const m = String(s).match(/access\/([a-f0-9]{16,})/i);
  return m ? m[1] : String(s).trim();
};

const sessionState = (s) => {
  const start = new Date(s.scheduled_at).getTime();
  const mins = parseInt(s.duration, 10) || 60;
  const now = Date.now();
  if (now >= start - 15 * 60000 && now <= start + mins * 60000) return "live";
  if (now < start) return "upcoming";
  return "ended";
};

const CAT_LABEL = { engineering: "Engineering", trading: "Trading", school: "School 1–12", mba: "MBA Finance", general: "All Students" };

export default function Live() {
  const [sessions, setSessions] = useState([]);
  const [joinUrls, setJoinUrls] = useState({});
  const [unlocked, setUnlocked] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get("/live-sessions").then((r) => setSessions(r.data)).catch(() => {});
    const saved = localStorage.getItem("pp_access_token");
    if (saved) unlock(saved, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = async (raw, silent = false) => {
    const token = extractToken(raw);
    if (!token) return;
    setChecking(true);
    try {
      const { data } = await api.get("/live-sessions/join", { params: { token } });
      const map = {};
      data.forEach((s) => { if (s.join_url) map[s.id] = s.join_url; });
      setJoinUrls(map);
      setUnlocked(true);
      localStorage.setItem("pp_access_token", token);
      if (!silent) toast.success("Verified — join buttons unlocked.");
    } catch {
      setUnlocked(false);
      localStorage.removeItem("pp_access_token");
      if (!silent) toast.error("That access link is invalid or the payment isn't verified yet.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid="live-page">
      <Header />
      <section className="px-6 pb-16 pt-32 md:px-12 md:pt-40">
        <div className="mx-auto max-w-[1600px]">
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">Real-time with the mentor</p>
          <h1 className="font-display text-6xl font-black uppercase leading-[0.85] tracking-tighter sm:text-7xl lg:text-8xl">
            Live<br /><span className="font-light text-zinc-500">Classes.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
            Weekly Zoom sessions for trading and school batches — market-open breakdowns, doubt rooms
            and strategy clinics. Join buttons unlock for enrolled students only.
          </p>
        </div>
      </section>

      {!unlocked && (
        <section className="px-6 pb-12 md:px-12">
          <div className="mx-auto max-w-[1600px]">
            <div className="flex flex-col gap-6 border border-white/10 bg-[#111111] p-8 md:flex-row md:items-end md:justify-between" data-testid="unlock-panel">
              <div>
                <div className="flex items-center gap-3 text-brand">
                  <KeyRound size={18} />
                  <span className="text-xs font-bold uppercase tracking-widest">Enrolled student?</span>
                </div>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-400">
                  Paste the access link you received right after your course purchase to unlock the Join buttons.
                </p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); unlock(tokenInput); }} className="flex w-full max-w-xl gap-3">
                <input
                  data-testid="unlock-token-input"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="Paste your access link or token"
                  className="w-full border-b border-[#27272A] bg-transparent py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white"
                />
                <button
                  data-testid="unlock-access-button"
                  type="submit"
                  disabled={checking}
                  className="flex shrink-0 items-center gap-2 bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50"
                >
                  {checking && <Loader2 size={14} className="animate-spin" />} Unlock
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-[1600px]">
          <div className="mb-10 flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-60" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-brand" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-400">Upcoming sessions</span>
          </div>
          <div className="flex flex-col gap-6" data-testid="sessions-list">
            {sessions.length === 0 && (
              <p className="border border-white/10 p-10 text-sm text-zinc-500" data-testid="sessions-empty">
                No sessions scheduled right now — new live classes are announced here first.
              </p>
            )}
            {sessions.map((s, i) => {
              const d = new Date(s.scheduled_at);
              const state = sessionState(s);
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.06, duration: 0.6, ease: EASE }}
                  className={`grid grid-cols-1 gap-6 border p-6 md:grid-cols-12 md:items-center md:p-8 ${state === "live" ? "border-brand/50 bg-[#140c08]" : "border-white/10 bg-[#0a0a0a]"} ${state === "ended" ? "opacity-45" : ""}`}
                  data-testid={`session-card-${s.id}`}
                >
                  <div className="flex items-center gap-4 md:col-span-2">
                    <div className="border border-white/10 px-4 py-3 text-center">
                      <div className="font-display text-3xl font-black tracking-tighter">{d.toLocaleDateString("en-IN", { day: "2-digit" })}</div>
                      <div className="text-xs uppercase tracking-widest text-brand">{d.toLocaleDateString("en-IN", { month: "short" })}</div>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      <span className="mt-1 flex items-center gap-1"><Clock size={11} /> {s.duration}</span>
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="flex flex-wrap items-center gap-2">
                      {state === "live" && (
                        <span className="animate-pulse bg-brand px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-white" data-testid={`session-live-badge-${s.id}`}>Live now</span>
                      )}
                      <span className="bg-white/5 px-2.5 py-1 text-xs uppercase tracking-widest text-zinc-400">{CAT_LABEL[s.category] || "All Students"}</span>
                      <span className="bg-white/5 px-2.5 py-1 text-xs uppercase tracking-widest text-zinc-400">{s.platform}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-bold tracking-tight md:text-2xl">{s.title}</h3>
                    {s.description && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-500">{s.description}</p>}
                  </div>
                  <div className="md:col-span-3 md:justify-self-end">
                    {unlocked && joinUrls[s.id] && state !== "ended" ? (
                      <a
                        href={joinUrls[s.id]}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`join-session-button-${s.id}`}
                        className="flex items-center gap-3 bg-brand px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95"
                      >
                        <Video size={18} /> Join on {s.platform}
                      </a>
                    ) : state === "ended" ? (
                      <span className="text-xs uppercase tracking-widest text-zinc-600">Session ended</span>
                    ) : (
                      <div className="flex items-center gap-2 border border-white/10 px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-600" data-testid={`join-locked-${s.id}`}>
                        <Lock size={14} /> Enrolled only
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
